import { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * LanguageMenu — 7-language premium picker.
 * Shows current language flag + 2-letter code in the trigger, opens a
 * glassmorphism dropdown listing all supported languages with high-res
 * flag PNGs from flagcdn (1×/2× srcSet).
 */
const LanguageMenu = ({ compact = false }) => {
  const { language, pickLanguage, supportedLanguages } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const current = supportedLanguages.find((l) => l.code === language) || supportedLanguages[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 ${compact ? 'h-8 px-2.5' : 'h-9 px-3'} rounded-full bg-white/[0.04] hover:bg-white/[0.10] ring-1 ring-white/10 text-slate-200 hover:text-white text-[11px] font-semibold transition-colors`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="language-menu-toggle"
      >
        <Globe className="w-3.5 h-3.5 text-indigo-200" strokeWidth={2.4} />
        <img
          src={`https://flagcdn.com/w40/${current.flag}.png`}
          srcSet={`https://flagcdn.com/w40/${current.flag}.png 1x, https://flagcdn.com/w80/${current.flag}.png 2x`}
          width="18"
          height="13"
          alt={current.english}
          className="h-[13px] w-[18px] rounded-[2px] object-cover ring-1 ring-white/20"
          loading="eager"
        />
        <span className="uppercase tracking-wider">{current.code}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute top-full mt-2 right-0 rtl:right-auto rtl:left-0 z-50 w-56
            rounded-2xl overflow-hidden
            bg-[rgba(13,17,23,0.85)] backdrop-blur-2xl
            ring-1 ring-white/10 border border-white/5
            shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
          role="menu"
          data-testid="language-menu-dropdown"
        >
          <div className="px-3 py-2 border-b border-white/5">
            <p className="text-[9px] font-bold tracking-[0.2em] uppercase text-indigo-300/80">
              Languages · 7
            </p>
          </div>
          <ul className="py-1.5 max-h-[60vh] overflow-y-auto">
            {supportedLanguages.map((l) => {
              const active = l.code === language;
              return (
                <li key={l.code}>
                  <button
                    type="button"
                    onClick={() => { pickLanguage(l.code); setOpen(false); }}
                    className={`group w-full flex items-center gap-2.5 px-3 py-2 text-left rtl:text-right transition-colors ${
                      active ? 'bg-indigo-500/15' : 'hover:bg-white/[0.05]'
                    }`}
                    data-testid={`language-option-${l.code}`}
                  >
                    <img
                      src={`https://flagcdn.com/w40/${l.flag}.png`}
                      srcSet={`https://flagcdn.com/w40/${l.flag}.png 1x, https://flagcdn.com/w80/${l.flag}.png 2x`}
                      width="22"
                      height="16"
                      alt={l.english}
                      className="h-[16px] w-[22px] rounded-[2px] object-cover ring-1 ring-white/15 shrink-0"
                      loading="lazy"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block text-[14px] font-semibold text-white truncate" dir={l.dir}>{l.label}</span>
                      <span className="block text-[10px] uppercase tracking-wider text-slate-400">{l.english}</span>
                    </span>
                    {active && <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LanguageMenu;
